import { useState, useEffect, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { Clock, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategorySlider = ({ category, icon, color, translatedCategory }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLang();
  
  // Use translated category name for display, fallback to original
  const displayCategory = translatedCategory || category;
  
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 2 },
      '(min-width: 1024px)': { slidesToScroll: 3 },
    }
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const fetchCategoryNews = async () => {
      try {
        const response = await axios.get(`${API}/news?category=${encodeURIComponent(category)}&limit=10&language=${lang}`);
        setNews(response.data);
      } catch (e) {
        console.error(`Error fetching ${category} news:`, e);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryNews();
  }, [category, lang]);

  if (loading) {
    return (
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xl">{icon}</span>
          <h2 className={`text-xl font-bold ${color}`}>{displayCategory}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-zinc-900/50 rounded-xl overflow-hidden border border-zinc-800/50 animate-pulse">
              <div className="aspect-video bg-zinc-800" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                <div className="h-3 bg-zinc-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (news.length === 0) return null;

  return (
    <div className="mb-10" data-testid={`category-slider-${category.toLowerCase().replace(/\s/g, '-')}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          <h2 className={`text-xl font-bold ${color}`}>{displayCategory}</h2>
          <span className="text-sm text-zinc-500">({news.length} {t('news')})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
              canScrollPrev 
                ? 'bg-zinc-800 hover:bg-zinc-700 text-white' 
                : 'bg-zinc-900 text-zinc-600 cursor-not-allowed'
            }`}
            data-testid={`${category.toLowerCase()}-slider-prev`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
              canScrollNext 
                ? 'bg-zinc-800 hover:bg-zinc-700 text-white' 
                : 'bg-zinc-900 text-zinc-600 cursor-not-allowed'
            }`}
            data-testid={`${category.toLowerCase()}-slider-next`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Slider */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-4">
          {news.map((item) => (
            <div 
              key={item.id} 
              className="flex-[0_0_85%] md:flex-[0_0_45%] lg:flex-[0_0_30%] min-w-0 pl-4"
            >
              <Link 
                to={`/haber/${item.slug || item.id}`}
                className="block group bg-zinc-900/50 rounded-xl overflow-hidden border border-zinc-800/50 hover:border-amber-500/30 transition-all h-full"
                data-testid={`category-news-card-${item.id}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=225&fit=crop';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-4">
                  <h3 className="text-base font-semibold text-white group-hover:text-amber-400 transition-colors line-clamp-2 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-zinc-400 text-sm line-clamp-2 mb-3 hidden md:block">
                    {item.summary}
                  </p>
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(item.created_at), "d MMM", { locale: tr })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {item.views}
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(CategorySlider);
